import { useCallback, useMemo } from 'react';

import { SchoolConnectExchangeResponse } from '../types';

/**
 * 첫 exchange 성공 응답을 sessionStorage에 캐시하는 hook.
 *
 * 동기:
 *  - exchange API는 authCode를 1회만 받는다 (BE 측 consume).
 *  - 미로그인 → /auth/login → /connect/resume 흐름에서 Resume이
 *    동일 authCode로 exchange를 다시 호출하면 410(AuthCodeExpiredOrUsed)이 발생한다.
 *  - 첫 exchange 성공 결과를 캐시해 두면 Resume은 캐시를 우선 사용해 그 흐름을 회피한다.
 *
 * 키:
 *  - `schoolConnect:exchangeResult` (단일 JSON)
 *
 * 만료:
 *  - 캐시 자체에 `expiresAt` (unix ms) 을 포함.
 *  - 캐시 hit 시점에 `expiresAt <= now` 면 만료로 간주 → 캐시 비우고 null 반환.
 *  - 캐시 만료 시 호출자는 exchange 재호출 (또는 expired 상태로 폴백).
 *
 * SSR/quota 안전성:
 *  - 모든 sessionStorage 접근은 try/catch 로 감싸 실패 시 조용히 무시.
 */
export const EXCHANGE_RESULT_STORAGE_KEY = 'schoolConnect:exchangeResult';

export interface StoredExchangeResult {
  authCode: string;
  data: SchoolConnectExchangeResponse;
  expiresAt: number; // unix ms timestamp
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * 캐시된 exchange 결과를 읽는다.
 *
 * - 캐시 없음 → `null`
 * - JSON parse 실패 → 캐시 정리 후 `null`
 * - 필수 필드 누락 → 캐시 정리 후 `null`
 * - 만료 (`expiresAt <= now`) → 캐시 정리 후 `null`
 * - 정상 → `StoredExchangeResult`
 *
 * `authCode` 매칭 검증은 호출자 책임 (Resume 흐름에서는 stored authCode와 일치할 때만 사용).
 */
export function readStoredExchangeResult(now: number = Date.now()): StoredExchangeResult | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(EXCHANGE_RESULT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredExchangeResult> | null;
    if (
      !parsed ||
      typeof parsed.authCode !== 'string' ||
      !parsed.data ||
      typeof parsed.data.email !== 'string' ||
      typeof parsed.data.nickname !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      !Number.isFinite(parsed.expiresAt)
    ) {
      clearStoredExchangeResult();
      return null;
    }

    if (parsed.expiresAt <= now) {
      clearStoredExchangeResult();
      return null;
    }

    return {
      authCode: parsed.authCode,
      data: parsed.data,
      expiresAt: parsed.expiresAt
    };
  } catch {
    // JSON.parse 실패 또는 storage 접근 실패 — 캐시를 비우고 null 반환
    clearStoredExchangeResult();
    return null;
  }
}

export function writeStoredExchangeResult(value: StoredExchangeResult): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(EXCHANGE_RESULT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // quota 초과 등 — 무시
  }
}

export function clearStoredExchangeResult(): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(EXCHANGE_RESULT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface UseExchangeCacheReturn {
  /**
   * 마운트 시점에 1회 동기적으로 읽은 캐시.
   * 캐시 만료/없음 → null.
   */
  cached: StoredExchangeResult | null;
  /** 캐시에 저장한다. */
  save: (value: StoredExchangeResult) => void;
  /** 캐시를 비운다. */
  clear: () => void;
}

/**
 * exchange 결과 캐시 hook.
 *
 * `useConnectAuthCode` 와 동일한 방식 — 마운트 시점에 1회 lazy read.
 * Resume 흐름에서만 의미가 있으므로, `/connect` 첫 진입 시에는 호출자가 save 만 한다.
 */
export function useExchangeCache(): UseExchangeCacheReturn {
  const cached = useMemo(() => readStoredExchangeResult(), []);

  const save = useCallback((value: StoredExchangeResult) => {
    writeStoredExchangeResult(value);
  }, []);

  const clear = useCallback(() => {
    clearStoredExchangeResult();
  }, []);

  return { cached, save, clear };
}
